// Code-based TanStack Router tree. The pathless `_app` layout guards every signed-in screen:
// setup first, then sign-in, then the shell.
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Link,
  Outlet,
  redirect,
  useRouter,
  type ErrorComponentProps,
} from "@tanstack/react-router";
import type { QueryClient } from "@tanstack/react-query";
import type { SessionInfo } from "@shared/api";
import { parseAccountsSearch } from "@web/accounts/accountsSearch";
import { FeedPage } from "@web/feed/FeedPage";
import { parseAuditSearch, parseFeedSearch } from "@web/feed/feedSearch";
import { parseListSearch, safeRedirect } from "@web/inbox/listSearch";
import { parseTasksSearch } from "@web/tasks/tasksSearch";
import { parseAskSearch } from "@web/ask/ask";
import { parseScreenerSearch } from "@web/screener/screener";
import { queryClient } from "@web/lib/queryClient";
import { sessionQuery } from "@web/lib/queries";
import {
  loadAccounts,
  loadAnswers,
  loadAsk,
  loadAudit,
  loadBrief,
  loadCalendarSettings,
  loadCard,
  loadDrafts,
  loadFocus,
  loadHidden,
  loadInbox,
  loadFreshStart,
  loadRecent,
  loadLearning,
  loadLogin,
  loadPrompts,
  loadScreener,
  loadSettings,
  loadSetup,
  loadSpend,
  loadMeasure,
  loadNotificationSettings,
  loadTasks,
  loadThread,
  loadVips,
  loadVoice,
  loadWelcome,
  loadWork,
} from "@web/screens";
import { setupQuery } from "@web/lib/setupApi";
import { parseWelcomeSearch } from "@web/setup/welcomeSearch";
import { AppShell } from "@web/shell/AppShell";
import { Button } from "@web/ui/Button";

export interface RouterContext {
  queryClient: QueryClient;
}

function loadSession(queryClient: QueryClient): Promise<SessionInfo> {
  return queryClient.ensureQueryData(sessionQuery);
}

/** The guided first run is waiting (a new owner who hasn't finished or skipped it yet). */
async function guidedSetupPending(queryClient: QueryClient): Promise<boolean> {
  try {
    return (await queryClient.ensureQueryData(setupQuery)).guided === "pending";
  } catch {
    return false; // An older server, or a hiccup: never block the app on it.
  }
}

function ServerUnreachable({ reset }: ErrorComponentProps) {
  const router = useRouter();
  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-3 bg-bg px-6 text-center">
      <p className="font-serif text-2xl">MeeMail can’t reach its server</p>
      <p className="max-w-sm text-muted">
        Nothing is lost. Check that the server is running, then try again.
      </p>
      <Button
        className="mt-2"
        onClick={() => {
          reset();
          void router.invalidate();
        }}
      >
        Try again
      </Button>
    </main>
  );
}

function NotFound() {
  return (
    <main className="flex h-full min-h-[50vh] flex-col items-center justify-center gap-2 px-6 text-center">
      <p className="font-serif text-2xl">Nothing here</p>
      <Link to="/feed" className="text-accent-text underline underline-offset-2">
        Go to the feed
      </Link>
    </main>
  );
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: Outlet,
  errorComponent: ServerUnreachable,
  notFoundComponent: NotFound,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/feed", replace: true });
  },
});

const setupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/setup",
  beforeLoad: async ({ context }) => {
    const session = await loadSession(context.queryClient);
    if (!session.setupRequired) {
      throw redirect({ to: session.authenticated ? "/feed" : "/login", replace: true });
    }
  },
  component: lazyRouteComponent(loadSetup, "SetupPage"),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (raw: Record<string, unknown>): { redirect?: string | undefined } => ({
    redirect: safeRedirect(raw.redirect),
  }),
  beforeLoad: async ({ context, search }) => {
    const session = await loadSession(context.queryClient);
    if (session.setupRequired) throw redirect({ to: "/setup", replace: true });
    if (session.authenticated) {
      throw redirect({ href: safeRedirect(search.redirect) ?? "/feed", replace: true });
    }
  },
  component: lazyRouteComponent(loadLogin, "LoginPage"),
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "_app",
  beforeLoad: async ({ context, location }) => {
    const session = await loadSession(context.queryClient);
    if (session.setupRequired) throw redirect({ to: "/setup", replace: true });
    if (!session.authenticated) {
      throw redirect({ to: "/login", search: { redirect: location.href }, replace: true });
    }
  },
  component: AppShell,
  notFoundComponent: NotFound,
});

const feedRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/feed",
  validateSearch: parseFeedSearch,
  // A new owner lands in the guided first run until it is finished or skipped.
  beforeLoad: async ({ context }) => {
    if (await guidedSetupPending(context.queryClient))
      throw redirect({ to: "/welcome", search: {}, replace: true });
  },
  component: FeedPage,
});

const welcomeRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/welcome",
  validateSearch: parseWelcomeSearch,
  component: lazyRouteComponent(loadWelcome, "WelcomePage"),
});

const cardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/feed/cards/$cardId",
  component: lazyRouteComponent(loadCard, "CardPage"),
});

const promptsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/feed/config",
  component: lazyRouteComponent(loadPrompts, "PromptsPage"),
});

const focusRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/focus",
  component: lazyRouteComponent(loadFocus, "FocusPage"),
});

const learningRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/learning",
  component: lazyRouteComponent(loadLearning, "LearningPage"),
});

const workRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/work",
  component: lazyRouteComponent(loadWork, "WorkPage"),
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit",
  validateSearch: parseAuditSearch,
  component: lazyRouteComponent(loadAudit, "AuditPage"),
});

const hiddenRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/hidden",
  component: lazyRouteComponent(loadHidden, "HiddenPage"),
});

const recentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/recent",
  component: lazyRouteComponent(loadRecent, "RecentPage"),
});

const freshStartRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/fresh-start",
  component: lazyRouteComponent(loadFreshStart, "FreshStartPage"),
});

const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/tasks",
  validateSearch: parseTasksSearch,
  component: lazyRouteComponent(loadTasks, "TasksPage"),
});

const latestBriefRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/brief",
  component: lazyRouteComponent(loadBrief, "LatestBriefPage"),
});

const briefRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/brief/$briefId",
  component: lazyRouteComponent(loadBrief, "BriefPage"),
});

const libraryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/saved",
  component: lazyRouteComponent(() => import("@web/library/LibraryPage"), "LibraryPage"),
});

const inboxRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inbox",
  validateSearch: parseListSearch,
  component: lazyRouteComponent(loadInbox, "InboxPage"),
});

const threadRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/t/$key",
  validateSearch: parseListSearch,
  component: lazyRouteComponent(loadThread, "ThreadPage"),
});

/** Task backlinks (e.g. from Todoist) name the thread as /t/:accountId/:gmailThreadId. */
const threadBacklinkRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/t/$accountId/$gmailThreadId",
  beforeLoad: ({ params }) => {
    throw redirect({
      to: "/t/$key",
      params: { key: `${params.accountId}:${params.gmailThreadId}` },
      replace: true,
    });
  },
});

const accountsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/accounts",
  validateSearch: parseAccountsSearch,
  // Google's connect flow returns here; during the guided first run it goes back to its step.
  beforeLoad: async ({ context, search }) => {
    if ((search.connected || search.error) && (await guidedSetupPending(context.queryClient)))
      throw redirect({
        to: "/welcome",
        search: { step: "gmail", connected: search.connected, error: search.error },
        replace: true,
      });
  },
  component: lazyRouteComponent(loadAccounts, "AccountsPage"),
});

const settingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings",
  component: lazyRouteComponent(loadSettings, "SettingsPage"),
});

const spendRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/spend",
  component: lazyRouteComponent(loadSpend, "SpendPage"),
});

const measureRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/measure",
  component: lazyRouteComponent(loadMeasure, "MeasurePage"),
});

const vipsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/vips",
  component: lazyRouteComponent(loadVips, "VipsPage"),
});

const notificationSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/notifications",
  component: lazyRouteComponent(loadNotificationSettings, "NotificationsPage"),
});

const voiceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/voice",
  component: lazyRouteComponent(loadVoice, "VoicePage"),
});

const calendarSettingsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/settings/calendar",
  component: lazyRouteComponent(loadCalendarSettings, "CalendarPage"),
});

const draftsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/compose",
  component: lazyRouteComponent(loadDrafts, "DraftsPage"),
});

const screenerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/screener",
  validateSearch: parseScreenerSearch,
  component: lazyRouteComponent(loadScreener, "ScreenerPage"),
});

const answersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/answers",
  component: lazyRouteComponent(loadAnswers, "AnswersPage"),
});

const askRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/ask",
  validateSearch: parseAskSearch,
  component: lazyRouteComponent(loadAsk, "AskPage"),
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  setupRoute,
  loginRoute,
  appRoute.addChildren([
    feedRoute,
    welcomeRoute,
    cardRoute,
    promptsRoute,
    focusRoute,
    learningRoute,
    workRoute,
    auditRoute,
    hiddenRoute,
    recentRoute,
    freshStartRoute,
    tasksRoute,
    latestBriefRoute,
    briefRoute,
    inboxRoute,
    libraryRoute,
    threadRoute,
    threadBacklinkRoute,
    accountsRoute,
    settingsRoute,
    spendRoute,
    measureRoute,
    vipsRoute,
    notificationSettingsRoute,
    voiceRoute,
    calendarSettingsRoute,
    screenerRoute,
    draftsRoute,
    answersRoute,
    askRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: false,
  scrollRestoration: false,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
